/**
 * Mock-«сервер» отзывов — временно, пока бэкенд не сделал ручки. Покупатель и
 * админка живут на разных доменах, поэтому и хранилища у них разные:
 *  - у покупателя — его оценки и отказы по заказам; какие заказы закрыты, mock
 *    узнаёт из списка заказов, который приложение уже получило от API;
 *  - у админки — стартовый набор отзывов (`seed.ts`): оценки покупателей до
 *    неё без API не доходят.
 */
import { ApiError } from '../../errors'
import {
  ADMIN_REVIEWS_PAGE_SIZE,
  REVIEW_ASK_DAYS,
  REVIEW_COMMENT_MAX,
  type AdminReviewsQuery,
  type MyReviewDto,
  type PendingReviewDto,
  type ReviewDto,
  type ReviewInput,
  type ReviewPageDto,
  type ReviewSummaryDto,
} from '../contract'
import { SEED_REVIEWS } from './seed'

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>

const DAY = 24 * 60 * 60 * 1000

export const MOCK_MY_REVIEWS_KEY = 'dorixona.mock.reviews.mine.v1'
export const MOCK_ADMIN_REVIEWS_KEY = 'dorixona.mock.reviews.admin.v1'

export function validateReview(input: ReviewInput): void {
  if (!Number.isInteger(input.rating) || input.rating < 1 || input.rating > 5) {
    throw new ApiError({ code: 'validation_failed', status: 400, message: 'Оценка — от 1 до 5' })
  }
  if (input.comment.length > REVIEW_COMMENT_MAX) {
    throw new ApiError({
      code: 'validation_failed',
      status: 400,
      message: `Комментарий длиннее ${REVIEW_COMMENT_MAX} символов`,
    })
  }
}

const reply = <T>(work: () => T) =>
  new Promise<T>((resolve) => {
    resolve(work())
  })

function readJson<T>(storage: Storage, key: string, fallback: T): T {
  try {
    const raw = storage.getItem(key)
    return raw === null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

/* ══ покупатель ═══════════════════════════════════════════════════════════ */

interface MyState {
  reviews: MyReviewDto[]
  /** Заказы, о которых больше не спрашиваем: «не сейчас». */
  dismissed: number[]
}

/** Что покупательский экран ждёт от источника отзывов. */
export interface MyReviewsBackend {
  /**
   * Закрытые заказы, которые ждут оценки. С API список даёт сервер; mock
   * выбирает из `closed` — закрытых заказов, уже полученных приложением.
   */
  pending: (closed: PendingReviewDto[]) => Promise<PendingReviewDto[]>
  submit: (orderId: number, input: ReviewInput) => Promise<MyReviewDto>
  dismiss: (orderId: number) => Promise<void>
  /** Оценка заказа; null — не оценивали. */
  forOrder: (orderId: number) => Promise<MyReviewDto | null>
}

export interface MockReviewsOptions {
  storage: Storage
  /** Часы приложения (мс). */
  now?: () => number
}

export function createMockMyReviews({
  storage,
  now = () => Date.now(),
}: MockReviewsOptions): MyReviewsBackend {
  const read = () => {
    const state = readJson<Partial<MyState> | null>(storage, MOCK_MY_REVIEWS_KEY, null)
    return {
      reviews: Array.isArray(state?.reviews) ? state.reviews : [],
      dismissed: Array.isArray(state?.dismissed) ? state.dismissed : [],
    }
  }
  const write = (state: MyState) => {
    storage.setItem(MOCK_MY_REVIEWS_KEY, JSON.stringify(state))
  }

  return {
    pending: (closed) =>
      reply(() => {
        const state = read()
        const since = now() - REVIEW_ASK_DAYS * DAY
        return closed
          .filter(
            (order) =>
              Date.parse(order.closed_at) >= since &&
              !state.dismissed.includes(order.order_id) &&
              !state.reviews.some((r) => r.order_id === order.order_id),
          )
          .sort((a, b) => b.closed_at.localeCompare(a.closed_at))
      }),

    submit: (orderId, input) =>
      reply(() => {
        validateReview(input)
        const state = read()
        if (state.reviews.some((r) => r.order_id === orderId)) {
          throw new ApiError({ code: 'conflict', status: 409, message: 'Заказ уже оценён' })
        }
        const review: MyReviewDto = {
          order_id: orderId,
          rating: input.rating,
          comment: input.comment.trim(),
          created_at: new Date(now()).toISOString(),
        }
        write({ ...state, reviews: [...state.reviews, review] })
        return review
      }),

    dismiss: (orderId) =>
      reply(() => {
        const state = read()
        if (!state.dismissed.includes(orderId)) {
          write({ ...state, dismissed: [...state.dismissed, orderId] })
        }
      }),

    forOrder: (orderId) => reply(() => read().reviews.find((r) => r.order_id === orderId) ?? null),
  }
}

/* ══ админка ══════════════════════════════════════════════════════════════ */

export interface AdminReviewsBackend {
  list: (query?: AdminReviewsQuery) => Promise<ReviewPageDto>
}

const round1 = (value: number) => Math.round(value * 10) / 10

export function summarize(reviews: ReviewDto[]): ReviewSummaryDto {
  const stores = new Map<
    string,
    { store_id: number | null; store_name: string; sum: number; count: number }
  >()
  for (const r of reviews) {
    const key = String(r.store_id)
    const row = stores.get(key) ?? {
      store_id: r.store_id,
      store_name: r.store_name,
      sum: 0,
      count: 0,
    }
    row.sum += r.rating
    row.count += 1
    stores.set(key, row)
  }
  const total = reviews.reduce((sum, r) => sum + r.rating, 0)
  return {
    count: reviews.length,
    avg: reviews.length ? round1(total / reviews.length) : 0,
    by_store: [...stores.values()]
      .map(({ sum, ...row }) => ({ ...row, avg: round1(sum / row.count) }))
      .sort((a, b) => a.avg - b.avg || a.store_name.localeCompare(b.store_name)),
  }
}

export function createMockAdminReviews({
  storage,
}: Pick<MockReviewsOptions, 'storage'>): AdminReviewsBackend {
  return {
    list: (query = {}) =>
      reply(() => {
        const stored = readJson<unknown>(storage, MOCK_ADMIN_REVIEWS_KEY, null)
        const all = Array.isArray(stored) ? (stored as ReviewDto[]) : SEED_REVIEWS
        const byStore = all.filter(
          (r) =>
            query.storeId === undefined || query.storeId === null || r.store_id === query.storeId,
        )
        const picked = byStore
          .filter((r) => !query.rating || r.rating === query.rating)
          .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
        const size = query.pageSize ?? ADMIN_REVIEWS_PAGE_SIZE
        const page = query.page ?? 1
        return {
          items: picked.slice((page - 1) * size, page * size),
          total: picked.length,
          // Полоса средних — по аптекам без фильтра оценки: иначе «средняя»
          // при выбранных пятёрках была бы всегда 5.
          summary: summarize(query.rating ? byStore : picked),
        }
      }),
  }
}
